import { Link, useParams } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import {
  Button,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import {
  clinicDisplayName,
  useClinic,
  useClinicMembers,
  useClinicProvisioningEvents,
  useClinicSubscription,
  useResendOwnerInvite,
} from '../../../features/clinics/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

import { ClinicStatusBadge } from './ClinicStatusBadge.js';

/**
 * Painel da clínica — detalhe consultivo (read-only) acessado a partir
 * do `LeadCustomerBanner` ou da `ClinicsListPage`.
 *
 * Conteúdo:
 *   - Header: nome (trade > legal) + status com peso visual + identificação fiscal
 *   - Seção subscription: plano, billing cycle, trial, IDs Asaas
 *   - Seção members: lista de owners/admins/etc com role + status
 *   - Seção provisioning timeline: cronologia de
 *     `clinic_provisioning_events` com status por etapa
 *
 * Mutations (resend welcome, suspender, etc) ficam para PRs futuras —
 * esta entrega responde ao escopo M1-01g.b: "reabilitar botões disabled
 * do `LeadCustomerBanner`". Botões agora têm pra onde levar (esta
 * página), e ela mostra o estado essencial sem prometer ações que
 * não temos backend pra executar.
 *
 * Strings em pt-BR vêm do namespace i18n `clinics` (regra inviolável
 * §5.3 do AGENTS.md).
 */
export function ClinicDetailPage() {
  const { t } = useT('clinics');
  const { id } = useParams<{ id: string }>();

  const clinic = useClinic(id);
  const subscription = useClinicSubscription(id);
  const members = useClinicMembers(id);
  const events = useClinicProvisioningEvents(id);
  const resendInvite = useResendOwnerInvite();

  // Owner é o member com role_key='clinic_owner'. Status 'invited'
  // significa que ainda não clicou o magic link de ativação — habilita
  // o botão "Reenviar email de ativação".
  const ownerMember = members.data?.find(
    (m) => m.role_key === 'clinic_owner' && m.status === 'invited',
  );
  const canResendInvite = Boolean(ownerMember && id);

  const handleResend = async () => {
    if (!id) return;
    await resendInvite.mutateAsync(
      { clinic_id: id },
      {
        onSuccess: (result) => {
          if (result.action === 'already_accepted') {
            toast.info(t('detail.members.resendInvite.alreadyAccepted'));
          } else if (result.email_status === 'sent') {
            toast.success(t('detail.members.resendInvite.successTitle'), {
              description: t('detail.members.resendInvite.successDescription'),
            });
          } else {
            toast.error(t('detail.members.resendInvite.failedTitle'), {
              description: t('detail.members.resendInvite.failedDescription'),
            });
          }
        },
        onError: (err) =>
          toast.error(t('detail.members.resendInvite.failedTitle'), {
            description: (err as Error).message,
          }),
      },
    );
  };

  if (clinic.isLoading) {
    return (
      <SuperadminShell>
        <PageContainer>
          <Skeleton className="h-32 w-full" />
        </PageContainer>
      </SuperadminShell>
    );
  }

  if (clinic.isError) {
    return (
      <SuperadminShell>
        <PageContainer>
          <p className="text-sm text-destructive">
            {t('detail.loadError', { message: (clinic.error as Error).message })}
          </p>
        </PageContainer>
      </SuperadminShell>
    );
  }

  if (!clinic.data) {
    return (
      <SuperadminShell>
        <PageContainer>
          <PageIntroCard
            eyebrow={
              <Link to="/superadmin/clinics" className="hover:underline">
                {t('detail.eyebrow')}
              </Link>
            }
            title={t('detail.notFound.title')}
            description={t('detail.notFound.description')}
          />
        </PageContainer>
      </SuperadminShell>
    );
  }

  const c = clinic.data;
  const displayName = clinicDisplayName(c);

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={
            <Link to="/superadmin/clinics" className="hover:underline">
              {t('detail.eyebrow')}
            </Link>
          }
          title={displayName}
          description={
            c.trade_name && c.trade_name !== c.legal_name
              ? t('detail.header.legalName', { name: c.legal_name })
              : undefined
          }
          meta={<ClinicStatusBadge status={c.status} />}
        >
          <dl className="grid gap-x-6 gap-y-3 pt-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">{t('detail.header.cnpj')}</dt>
              <dd className="font-mono">{c.cnpj || '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('detail.header.email')}</dt>
              <dd className="break-all">{c.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('detail.header.phone')}</dt>
              <dd>{c.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('detail.header.consulting')}</dt>
              <dd>
                {c.has_consulting
                  ? t('detail.header.consultingTier', { tier: c.consulting_tier })
                  : t('detail.header.consultingNone')}
              </dd>
            </div>
          </dl>
        </PageIntroCard>

        {/* ── Subscription ─────────────────────────────────────────────── */}
        <PageSectionCard
          title={t('detail.subscription.title')}
          description={t('detail.subscription.description')}
        >
          {subscription.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : subscription.isError ? (
            <p className="text-sm text-destructive">
              {t('detail.subscription.loadError', {
                message: (subscription.error as Error).message,
              })}
            </p>
          ) : !subscription.data ? (
            <p className="text-sm text-muted-foreground">{t('detail.subscription.empty')}</p>
          ) : (
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.plan')}</dt>
                <dd className="font-medium text-foreground">
                  {subscription.data.plan?.name_pt_br ?? '—'}
                </dd>
                {subscription.data.plan ? (
                  <dd className="text-xs text-muted-foreground">
                    {t('detail.subscription.planDetail', {
                      tier: subscription.data.plan.tier,
                      price: formatBrl(subscription.data.plan.monthly_price_brl),
                    })}
                  </dd>
                ) : null}
              </div>
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.status')}</dt>
                <dd className="font-medium text-foreground">
                  {t(`status.${subscription.data.subscription.status}`, {
                    defaultValue: subscription.data.subscription.status,
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.billingCycle')}</dt>
                <dd>{subscription.data.subscription.billing_cycle}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.trial')}</dt>
                <dd>
                  {subscription.data.subscription.trial_ends_at
                    ? t('detail.subscription.trialUntil', {
                        date: formatDateBR(subscription.data.subscription.trial_ends_at),
                      })
                    : t('detail.subscription.trialNone')}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.currentPeriod')}</dt>
                <dd>
                  {formatDateBR(subscription.data.subscription.current_period_start)} →{' '}
                  {formatDateBR(subscription.data.subscription.current_period_end)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('detail.subscription.asaasIds')}</dt>
                <dd className="font-mono text-xs">
                  {t('detail.subscription.asaasCustomer', {
                    id: subscription.data.subscription.asaas_customer_id ?? '—',
                  })}
                  <br />
                  {t('detail.subscription.asaasSubscription', {
                    id: subscription.data.subscription.asaas_subscription_id ?? '—',
                  })}
                </dd>
              </div>
            </dl>
          )}
        </PageSectionCard>

        {/* ── Members ──────────────────────────────────────────────────── */}
        <PageSectionCard
          title={t('detail.members.title')}
          description={t('detail.members.description')}
        >
          {/* Banner de "aguardando ativação" + botão "Reenviar email"
              aparecem QUANDO existe owner com status='invited'. Útil
              quando o magic link expirou (TTL Supabase 1h), email caiu
              em spam, ou Karla quer disparar manualmente. Idempotente
              no backend — se já aceitou, retorna 'already_accepted'. */}
          {canResendInvite ? (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
              <div>
                <p className="font-semibold text-foreground">
                  {t('detail.members.resendInvite.bannerTitle')}
                </p>
                <p className="text-muted-foreground">
                  {t('detail.members.resendInvite.bannerDescription')}
                </p>
              </div>
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={handleResend}
                disabled={resendInvite.isPending}
              >
                {resendInvite.isPending
                  ? t('detail.members.resendInvite.sending')
                  : t('detail.members.resendInvite.cta')}
              </Button>
            </div>
          ) : null}

          {members.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : members.isError ? (
            <p className="text-sm text-destructive">
              {t('detail.members.loadError', { message: (members.error as Error).message })}
            </p>
          ) : (members.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.members.empty')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {members.data!.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                >
                  <div className="space-y-0.5">
                    <p className="font-mono text-xs text-muted-foreground">{m.user_id}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('detail.members.linkedSince', { date: formatDateBR(m.created_at) })}
                      {m.accepted_at
                        ? ` · ${t('detail.members.acceptedAt', { date: formatDateBR(m.accepted_at) })}`
                        : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {m.role_key}
                    </span>
                    <span className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {m.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PageSectionCard>

        {/* ── Provisioning timeline ────────────────────────────────────── */}
        <PageSectionCard
          title={t('detail.timeline.title')}
          description={t('detail.timeline.description')}
        >
          {events.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : events.isError ? (
            <p className="text-sm text-destructive">
              {t('detail.timeline.loadError', { message: (events.error as Error).message })}
            </p>
          ) : (events.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.timeline.empty')}</p>
          ) : (
            <ol className="space-y-3">
              {events.data!.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-3 text-sm"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span aria-hidden="true">
                        {e.status === 'success' ? '✅' : e.status === 'failed' ? '⚠️' : '⏳'}
                      </span>
                      <span className="font-medium text-foreground">
                        {t(`provisioningSteps.${e.step}`, { defaultValue: e.step })}
                      </span>
                    </div>
                    {e.error_message ? (
                      <p className="text-xs text-destructive">{e.error_message}</p>
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTimeBR(e.created_at)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </PageSectionCard>
      </PageContainer>
    </SuperadminShell>
  );
}

function formatBrl(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  try {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${value}`;
  }
}

function formatDateBR(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}

function formatDateTimeBR(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}
